import { HolidayFormData } from '@/types/holiday';

/**
 * Converte uma data no formato ISO "AAAA-MM-DD" para o formato brasileiro "DD/MM/AAAA".
 *
 * A conversão é feita por manipulação de string (sem `new Date(...)`) para evitar
 * qualquer deslocamento de fuso horário — feriados são datas de calendário em
 * horário local de Brasília, sem conversão para UTC.
 *
 * Retorna a string original caso ela não esteja no formato esperado.
 */
export function formatHolidayDate(date: string | null | undefined): string {
  if (!date) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!match) return date;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/**
 * Verifica se uma string representa uma data de calendário válida no formato "AAAA-MM-DD".
 * Rejeita datas sintaticamente válidas porém inexistentes (ex.: 2026-02-30).
 */
export function isValidIsoDate(date: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return (
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
  );
}

/**
 * Valida o formulário de feriado e retorna um mapa de erros por campo.
 * Um mapa vazio significa que o formulário é válido.
 */
export function validateHolidayForm(formData: HolidayFormData): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!formData.date || !formData.date.trim()) {
    errors.date = 'Data é obrigatória';
  } else if (!isValidIsoDate(formData.date.trim())) {
    errors.date = 'Data inválida';
  }

  if (!formData.description || !formData.description.trim()) {
    errors.description = 'Descrição é obrigatória';
  } else if (formData.description.length > 150) {
    errors.description = 'Descrição deve ter no máximo 150 caracteres';
  }

  return errors;
}
